<template>
  <div class="InsurancePersonalDataStep3Form">
    <h2 class="gl_form-title small">
      {{ title }}
    </h2>

    <RadioButtonGroup
      :legend="houseLabel"
      id="house-type"
      :value="houseType"
      v-model="houseType"
      :options="houseTypes"
      flexDirection="row"
      :editable="editable && !isShowingPossibleAddresses"
      :rules="[validationRules.radioRequired]"
    />

    <BaseAutocompleteInput
      id="address"
      :label="addressLabel"
      :placeholder="addressPlaceholder"
      :items="streets"
      v-model="street"
      :maxlength="60"
      :loading="searching"
      :editable="editable && !isShowingPossibleAddresses"
      @update="searchAddress"
      :rules="[validationRules.required]"
    />

    <BaseInput
      :label="t('hiring.common.insurance-personal-data-step-3.label-roadNumber')"
      id="road-number"
      v-model="roadNumber"
      :maxlength="5"
      :editable="editable && !isShowingPossibleAddresses"
      :placeholder="numberPlaceholder"
      :rules="[validationRules.required]"
    />

    <div
      v-if="houseType === configurations.houseTypes.apartment.id"
      class="row"
    >
      <BaseInput
        :label="t('hiring.common.insurance-personal-data-step-3.label-gateway')"
        id="gateway"
        v-model="gateway"
        :maxlength="5"
        :editable="editable && !isShowingPossibleAddresses"
        :placeholder="t('hiring.common.insurance-personal-data-step-3.placeholder-gateway')"
      />
      <BaseInput
        :label="t('hiring.common.insurance-personal-data-step-3.label-stair')"
        id="stair"
        v-model="stair"
        :maxlength="5"
        :editable="editable && !isShowingPossibleAddresses"
        :placeholder="t('hiring.common.insurance-personal-data-step-3.placeholder-stair')"
      />

      <BaseInput
        :label="t('hiring.common.insurance-personal-data-step-3.label-floor')"
        id="floor"
        v-model="floor"
        :maxlength="5"
        :editable="editable && !isShowingPossibleAddresses"
        :placeholder="t('hiring.common.insurance-personal-data-step-3.placeholder-floor')"
        :rules="[validationRules.required]"
      />

      <BaseInput
        :label="t('hiring.common.insurance-personal-data-step-3.label-doorway')"
        id="doorway"
        :maxlength="5"
        v-model="doorway"
        :editable="editable && !isShowingPossibleAddresses"
        :placeholder="t('hiring.common.insurance-personal-data-step-3.placeholder-doorway')"
      />
    </div>

    <BaseInput
      :label="t('hiring.common.insurance-personal-data-step-3.label-restAddress')"
      id="rest-number"
      v-model="addressExtra"
      :maxlength="50"
      :editable="editable && !isShowingPossibleAddresses"
      :placeholder="t('hiring.common.insurance-personal-data-step-3.placeholder-restAddress')"
    />

    <AddressProposals
      v-if="isShowingPossibleAddresses && editable"
      ref="proposals"
      v-model="addressSelected"
      @clear="clearOptions"
      :generatedAddress="generatedAddress as AddressDetails"
      :normalizedAddress="normalizedAddress as AddressDetails"
    />

    <div
      ref="hiddenFieldWrapper"
      style="display: none"
      aria-hidden="true"
    >
      <v-text-field
        ref="$field"
        id="validate-button"
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="validAddressValidation"
        :rules="[validationRules.validate]"
        :hide-details="true"
      />
    </div>

    <div class="actions">
      <template v-if="!isShowingPossibleAddresses">
        <BaseButton @click="generateOptions">
          {{ t('copy_address-form_button_validate') }}
        </BaseButton>

        <ErrorMessageComponent
          v-if="fieldValidationErrorMessage"
          :messages="fieldValidationErrorMessage"
        />
      </template>

      <BaseButton
        v-else
        @click="emit('next')"
        :name="gtmPrefix + NEXT_BTN"
        :id="gtmPrefix + NEXT_BTN"
        :emitGTMEvent="{ event: GTMEventType.NEXT }"
      >
        {{ t('rater.base.next') }}
      </BaseButton>

      <BaseButton
        type="outlined"
        icon="icon-arrow-left"
        @click="emit('previous')"
        :name="gtmPrefix + BACK_BTN"
        :id="gtmPrefix + BACK_BTN"
        :emitGTMEvent="{ event: GTMEventType.BACK }"
      >
        {{ t('rater.base.back') }}
      </BaseButton>

      <BaseButton
        type="transparent"
        icon="icon-save-02"
        :disabled="!isValidForm"
        @click="saveAndExit"
      >
        {{ t('hiring.base.save-and-exit') }}
      </BaseButton>
    </div>

    <ModalComponent
      class="cp-modal"
      :value="supportModalStore.openModal"
      @update:value="closeModal"
      @click:outside="supportModalStore.setOpenModal(true)"
    >
      <i class="icon-advertencia"></i>
      <h2 class="gl_form-title small">
        {{ t('copy_validate_address_modal_title') }}
      </h2>
      <p>{{ t('copy_validate_address_modal_description') }}</p>

      <div class="actions">
        <BaseButton @click="supportModalStore.setOpenModal(false)"
          >{{ t('copy_button_return') }}
        </BaseButton>
        <BaseButton
          type="transparent"
          @click="goToStep0()"
        >
          {{ t('copy_validate_address_modal_button') }}
        </BaseButton>
      </div>
    </ModalComponent>
  </div>
</template>

<script lang="ts" setup>
  import Insured from '@/modules/common/common/models/APP/Insured'
  import AddressProposals from '@/modules/common/hiring/components/AddressProposals.vue'
  import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
  import ErrorMessageComponent from '@/modules/core/components/formFields/ErrorMessageComponent.vue'
  import BaseAutocompleteInput from '@/modules/core/components/ui/BaseAutocompleteInput.vue'
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import BaseInput from '@/modules/core/components/ui/BaseInput.vue'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import ModalComponent from '@/modules/core/components/utils/ModalComponent.vue'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useCoolDown } from '@/modules/core/composables/useCoolDown'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import { useScroll } from '@/modules/core/composables/useScroll'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import BranchEnum from '@/modules/core/models/BranchEnum'
  import { AddressTypeEnum, UserRoleEnum } from '@/modules/core/models/enums'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { BACK_BTN, NEXT_BTN } from '@/modules/core/models/IdHTMLItems'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import { isBigger } from '@/utils/date.utils'
  import { computed, inject, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'

  const { t } = useI18n()
  const commonStore = useCommonStore()
  const loadingStore = useLoadingStore()
  const { runCoolDown } = useCoolDown()
  const { scrollToTop, scrollToId } = useScroll()
  const { required } = useRules()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    isValidForm: boolean
    extra?: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const emit = defineEmits(['next', 'previous', 'save-and-exit', 'redirect-to'])
  const validationRules = {
    required,
    radioRequired: (value: string) => required(value, 'common.validations.option'),
    validate: (value: string) => required(value, 'Tienes que validar los datos')
  }

  const validateForm = inject<() => Promise<boolean>>('validateForm') ?? (async () => true)

  const supportModalStore = useSupportModalStore()

  const houseTypes = ref<OptionsValuesInterface[]>(props.configurations.houseTypesOptions)
  const currentInsured = new Insured(
    props.extra?.customInsuredProp
      ? props.answers[props.extra.customInsuredProp]
      : props.answers.currentInsured
  )
  const currentInsuredAddress = currentInsured.addressDetails
  const validHouseValue = houseTypes.value.some((t) => t.value === currentInsuredAddress.houseType)
  const editable = ref(currentInsuredAddress.editable ?? true)
  const houseType = ref(
    validHouseValue ? currentInsuredAddress.houseType : props.configurations.defaultHouseType
  )
  const city = ref(currentInsuredAddress.city)
  const cityCode = ref(currentInsuredAddress.cityCode)
  const postalCode = ref(currentInsuredAddress.postalCode)
  const gateway = ref(currentInsuredAddress.gateway)
  const stair = ref(currentInsuredAddress.stair)
  const floor = ref(currentInsuredAddress.floor)
  const doorway = ref(currentInsuredAddress.doorway)
  const street = ref(currentInsuredAddress.address!)
  const roadNumber = ref(currentInsuredAddress.roadNumber)
  const addressExtra = ref(currentInsuredAddress.addressExtra)

  const proposals = ref()

  const streets = ref<string[]>([])
  const searching = ref(false)

  const isShowingPossibleAddresses = ref(!editable.value)
  const normalizedAddress = ref<AddressDetails | null>(null)
  const generatedAddress = ref<AddressDetails | null>(null)
  const addressSelected = ref(currentInsuredAddress.type)
  const minorInsured = ref(!isCurrentInsuredMoreThan18())
  const tryToSaveAndExit = ref(false)

  const insuredName = ref(currentInsured.userDetails.name)
  const isHouse = computed(() => houseType.value === props.configurations.houseTypes.house.id)
  const validAddressValidation = computed(
    () => !tryToSaveAndExit.value || isShowingPossibleAddresses.value
  )

  const gtmPrefix = 'insurance_personal_data_'

  function searchAddress(value: string) {
    if (!value) return

    searching.value = true
    runCoolDown(loadAddress, { partialStreet: value })
  }

  async function loadAddress({ partialStreet }: { partialStreet: string }) {
    if (typeof partialStreet !== 'string') {
      searching.value = false
      return
    }

    if (partialStreet.length < 2) {
      searching.value = false
      streets.value = []
      return
    }

    const currentInsuredAddress = currentInsured.addressDetails
    const { provinceCode } = currentInsuredAddress
    const { values, aborted } = await commonStore.findStreets(
      provinceCode!,
      cityCode.value!,
      partialStreet
    )

    if (aborted) return

    streets.value = values
    searching.value = aborted
  }

  async function getNormalizedAddress() {
    const addressRequest = {
      street: street.value!,
      city: city.value!,
      zip: postalCode.value!,
      house: roadNumber.value!
    }
    const { result, address } = await commonStore.normalizeAddress(addressRequest)

    if (result === 0) return null

    return getAddressByHouseType({
      address: address?.street || addressRequest.street,
      roadNumber: address?.house || addressRequest.house,
      streetType: address?.streetType,
      zip: address?.zip || addressRequest.zip
    })
  }

  function getGeneratedAddress() {
    return getAddressByHouseType({
      address: street.value,
      roadNumber: roadNumber.value,
      streetType: currentInsuredAddress.streetType
    })
  }

  function getAddressByHouseType(address: any) {
    const addressData = new AddressDetails(currentInsured.addressDetails)

    let commonData: any = {
      ...currentInsured.addressDetails,
      houseType: houseType.value,
      addressExtra: addressExtra.value,
      streetType: address?.streetType ?? '', //? Remove?
      gateway: null,
      stair: null,
      floor: null,
      doorway: null
    }
    if (address?.zip) commonData['postalCode'] = address.zip

    addressData.updateAttributes({ ...commonData, ...address })

    if (isHouse.value) {
      return addressData
    }

    addressData.updateAttributes({
      gateway: gateway.value,
      stair: stair.value,
      floor: floor.value,
      doorway: doorway.value
    })

    return addressData
  }

  async function generateOptions() {
    tryToSaveAndExit.value = false

    const valid = await validateForm()
    if (!valid) {
      return
    }

    normalizedAddress.value = await getNormalizedAddress()
    generatedAddress.value = getGeneratedAddress()
    isShowingPossibleAddresses.value = true
    setTimeout(() => scrollToId('location'), 100)
  }

  function clearOptions() {
    normalizedAddress.value = null
    generatedAddress.value = null
    addressSelected.value = null
    isShowingPossibleAddresses.value = false

    scrollToTop()
  }

  const title = computed(() => {
    const name = insuredName.value
    const i18n = getLabelSuffixByRole(currentInsured.role)

    return t(`hiring.common.insurance-personal-data-step-3.form-legend.${i18n}`, { name })
  })

  const houseLabel = computed(() => {
    const i18n = getLabelSuffixByRole(currentInsured.role)
    return t(`hiring.common.insurance-personal-data-step-3.label-house-type.${i18n}`)
  })

  const addressLabel = computed(() => {
    const i18n = getLabelSuffixByRole(currentInsured.role)
    return t(`hiring.common.insurance-personal-data-step-3.label-address.${i18n}`)
  })

  const addressPlaceholder = computed(() => {
    const i18n = getLabelSuffixByRole(currentInsured.role)
    return t(`hiring.common.insurance-personal-data-step-3.placeholder-address.${i18n}`)
  })

  const numberPlaceholder = computed(() => {
    const i18n = getLabelSuffixByRole(currentInsured.role)
    return t(`hiring.common.insurance-personal-data-step-3.placeholder-roadNumber.${i18n}`)
  })

  function getLabelSuffixByRole(role: UserRoleEnum) {
    const isInsured = role === UserRoleEnum.INSURED
    return isInsured ? 'not-holder' : 'holder'
  }

  function isCurrentInsuredMoreThan18() {
    const birthDate = currentInsured.userDetails.birthDate
    return !birthDate || isBigger(birthDate, 18)
  }

  async function saveAndExit() {
    if (minorInsured.value) {
      tryToSaveAndExit.value = true
      const valid = await validateForm()
      if (!valid) return
    }

    emit('save-and-exit')
  }

  async function triggerSubmit() {
    const page = props.nextForm[0]

    if (!editable.value) {
      return { page }
    }

    const addressType = addressSelected.value ?? AddressTypeEnum.GENERATED
    const isNormalized = addressType === AddressTypeEnum.NORMALIZED
    let addressDetails = isNormalized ? normalizedAddress.value : generatedAddress.value
    if (!addressDetails) {
      addressDetails = getGeneratedAddress()
    }
    addressDetails!.updateAttributes({ type: addressType, addressNormalize: isNormalized })

    currentInsured.addressDetails = new AddressDetails(addressDetails)
    const answers = props.extra?.customInsuredProp
      ? { [props.extra.customInsuredProp]: currentInsured }
      : props.extra?.currentInsuredIndex === 'holder'
      ? { holder: currentInsured }
      : { addressDetails: currentInsured.addressDetails }

    return { page, answers }
  }

  function closeModal(value: boolean) {
    if (!value) {
      supportModalStore.setOpenModal(value)
    }
  }

  async function beforeMount() {
    if (!cityCode.value) {
      const addressDetails = currentInsured.addressDetails
      const { provinceCode, city } = addressDetails
      const { values } = await commonStore.findCities(provinceCode!, city!)
      cityCode.value = values[0].cityCode
    }

    loadingStore.stopLoading()
    if (currentInsured.addressDetails.address) {
      generateOptions()
    }
  }

  function goToStep0() {
    supportModalStore.setOpenModal(false)
    emit('redirect-to', 'Step0_1_StartDateForm')
  }

  onBeforeMount(beforeMount)

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let observer: MutationObserver | null = null

  onMounted(() => {
    observer = startDuplicatedLabelControl([hiddenFieldWrapper.value])
  })

  onBeforeUnmount(() => {
    endDuplicatedLabelControl(observer)
  })

  watch(addressSelected, (selected) => {
    if (
      [BranchEnum.DENTAL, BranchEnum.PETS, BranchEnum.TRAVEL, BranchEnum.LIFE].includes(
        props.configurations.businessLine
      )
    ) {
      return
    }

    const isInsured = currentInsured.role === UserRoleEnum.INSURED
    const isHolderInsured = currentInsured.role === UserRoleEnum.HOLDER_INSURED
    const addressNormalized = selected === AddressTypeEnum.NORMALIZED
    const postalCodeDifferent = normalizedAddress.value?.postalCode !== postalCode.value

    if ((isInsured || isHolderInsured) && addressNormalized && postalCodeDifferent) {
      addressSelected.value = null
      proposals.value.clearValue()
      supportModalStore.setOpenModal(true)
    }
  })

  const { $field, fieldValidationErrorMessage } = useValidationError()
</script>

<style lang="scss" scoped>
  .InsurancePersonalDataStep3Form {
    .row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: $gap-m;
    }

    .line {
      border: 1px $color-neutral-light solid;
      margin: $spacing-xl 0;
    }
  }

  .cp-modal {
    display: flex;
    flex-direction: column;
    height: $size-screen-h;

    i {
      font-size: $font-size-80;
      margin: $spacing-72 0 $spacing-44;
      text-align: center;
      display: block;
    }

    h2 {
      font-weight: $font-weight-700;
      max-width: $size-600;
      text-align: center;
      margin: 0 auto;
    }

    p {
      margin: $spacing-xl auto $spacing-74;
      text-align: center;
      font-size: $font-size-l;
      color: $color-neutral-dark;
      line-height: $line-height-130p;
    }

    .actions {
      margin-top: auto;

      @include breakpoint-md {
        margin-top: $spacing-l;
      }
    }
  }
</style>
